import React, { useState, useEffect } from 'react';
import {
  Card,
  List,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Select,
  Input,
  Badge,
  Avatar,
  Dropdown,
  Modal,
  message,
  Empty,
  Spin
} from 'antd';
import {
  BellOutlined,
  SearchOutlined,
  MoreOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  StopOutlined,
  TeamOutlined,
  UserOutlined,
  DollarOutlined,
  SettingOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  notificationService, 
  Notification, 
  NotificationFilters 
} from '../../services/notificationService';

const { Title, Text } = Typography;
const { Option } = Select;

const SuperAdminNotifications: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const queryClient = useQueryClient();

  // Debounce search text
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [typeFilter, categoryFilter, statusFilter]);

  // Construire les filtres pour l'API
  const filters: NotificationFilters = {
    page: currentPage,
    limit: pageSize,
    ...(debouncedSearchText && { search: debouncedSearchText }),
    ...(typeFilter && { type: typeFilter as any }),
    ...(categoryFilter && { category: categoryFilter as any }),
    ...(statusFilter === 'read' && { read: true }),
    ...(statusFilter === 'unread' && { read: false }),
  };

  // Récupération des notifications avec React Query
  const { data: notificationsData, isLoading: notificationsLoading, error: notificationsError } = useQuery({
    queryKey: ['notifications', filters],
    queryFn: () => notificationService.getNotifications(filters),
    staleTime: 30000, // 30 secondes
  });

  // Récupération des statistiques
  const { data: stats } = useQuery({
    queryKey: ['notification-stats'],
    queryFn: () => notificationService.getNotificationStats(),
    staleTime: 60000, // 1 minute
  });

  const notifications = notificationsData?.data || [];
  const pagination = notificationsData?.pagination;

  // Mutations pour les actions
  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => notificationService.markAsRead(id),
    onSuccess: () => {
      message.success('Notification marquée comme lue');
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-stats'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la mise à jour');
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      message.success('Toutes les notifications marquées comme lues');
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-stats'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la mise à jour');
    },
  });

  const deleteNotificationMutation = useMutation({
    mutationFn: (id: string) => notificationService.deleteNotification(id),
    onSuccess: () => {
      message.success('Notification supprimée');
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-stats'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la suppression');
    },
  });

  // Utilitaires
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckOutlined style={{ color: '#52c41a' }} />;
      case 'warning':
        return <WarningOutlined style={{ color: '#faad14' }} />;
      case 'error':
        return <StopOutlined style={{ color: '#ff4d4f' }} />;
      case 'info':
      default:
        return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'tenant':
        return <TeamOutlined />;
      case 'user':
        return <UserOutlined />;
      case 'payment':
        return <DollarOutlined />;
      case 'security':
        return <ExclamationCircleOutlined />;
      case 'system':
      default:
        return <SettingOutlined />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return '#ff4d4f';
      case 'high':
        return '#faad14';
      case 'medium':
        return '#1890ff';
      case 'low':
      default:
        return '#52c41a';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `il y a ${diffInMinutes} min`;
    } else if (diffInMinutes < 1440) { // 24 hours
      return `il y a ${Math.floor(diffInMinutes / 60)}h`;
    } else {
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  // Gestionnaires d'événements
  const handleMarkAsRead = (notificationId: string) => {
    markAsReadMutation.mutate(notificationId);
  };

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const handleDelete = (notificationId: string) => {
    Modal.confirm({
      title: 'Supprimer la notification',
      content: 'Êtes-vous sûr de vouloir supprimer cette notification ?',
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: () => deleteNotificationMutation.mutate(notificationId),
    });
  };

  const handleViewDetails = (notification: Notification) => {
    setSelectedNotification(notification);
    setDetailModalVisible(true);
    if (!notification.read) {
      handleMarkAsRead(notification.id);
    }
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
    queryClient.invalidateQueries({ queryKey: ['notification-stats'] });
    message.info('Notifications actualisées');
  };

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size) setPageSize(size);
  };

  const getActionItems = (notification: Notification) => [
    {
      key: 'view',
      icon: <EyeOutlined />,
      label: 'Voir les détails',
      onClick: () => handleViewDetails(notification),
    },
    ...(!notification.read ? [{
      key: 'read',
      icon: <CheckOutlined />,
      label: 'Marquer comme lu',
      onClick: () => handleMarkAsRead(notification.id),
    }] : []),
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Supprimer',
      danger: true,
      onClick: () => handleDelete(notification.id),
    },
  ];

  // Gestion du loading et des erreurs
  if (notificationsError) {
    return (
      <div style={{ padding: '24px' }}>
        <Card>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Title level={4} type="danger">Erreur lors du chargement</Title>
            <Text type="secondary">
              Impossible de charger les notifications. Veuillez réessayer.
            </Text>
            <br />
            <Button 
              type="primary" 
              style={{ marginTop: '16px' }}
              onClick={() => queryClient.invalidateQueries({ queryKey: ['notifications'] })}
            >
              Réessayer
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Centre de Notifications</Title>
        <Text type="secondary">
          Toutes les notifications et alertes de la plateforme FleetBase
        </Text>
      </div>

      {/* Statistiques */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total"
              value={stats?.total || 0}
              prefix={<BellOutlined />}
              valueStyle={{ color: '#1890ff' }}
              loading={!stats}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Non lues"
              value={stats?.unread || 0}
              prefix={<Badge status="processing" />}
              valueStyle={{ color: '#faad14' }}
              loading={!stats}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Urgentes"
              value={stats?.urgent || 0}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
              loading={!stats}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Action requise"
              value={stats?.action_required || 0}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#faad14' }}
              loading={!stats}
            />
          </Card>
        </Col>
      </Row>

      {/* Filtres et actions */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space size="middle">
              <Input
                placeholder="Rechercher dans les notifications..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 300 }}
              />
              <Select
                placeholder="Type"
                value={typeFilter}
                onChange={setTypeFilter}
                style={{ width: 120 }}
                allowClear
              >
                <Option value="info">Info</Option>
                <Option value="success">Succès</Option>
                <Option value="warning">Avertissement</Option>
                <Option value="error">Erreur</Option>
              </Select>
              <Select
                placeholder="Catégorie"
                value={categoryFilter}
                onChange={setCategoryFilter}
                style={{ width: 130 }}
                allowClear
              >
                <Option value="system">Système</Option>
                <Option value="tenant">Entreprise</Option>
                <Option value="user">Utilisateur</Option>
                <Option value="payment">Paiement</Option>
                <Option value="security">Sécurité</Option>
              </Select>
              <Select
                placeholder="Statut"
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: 120 }}
                allowClear
              >
                <Option value="unread">Non lues</Option>
                <Option value="read">Lues</Option>
              </Select>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                loading={notificationsLoading}
              >
                Actualiser
              </Button>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleMarkAllAsRead}
                disabled={(stats?.unread || 0) === 0}
                loading={markAllAsReadMutation.isPending}
              >
                Tout marquer comme lu
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Liste des notifications */}
      <Card>
        {notificationsLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>
              <Text type="secondary">Chargement des notifications...</Text>
            </div>
          </div>
        ) : notifications.length > 0 ? (
          <>
            <List
              dataSource={notifications}
              renderItem={(notification) => (
                <List.Item
                  style={{
                    backgroundColor: notification.read ? 'transparent' : '#f6ffed',
                    border: notification.priority === 'urgent' ? '1px solid #ff4d4f' : 'none',
                    borderRadius: '4px',
                    marginBottom: '8px',
                    padding: '16px',
                  }}
                  actions={[
                    <Dropdown
                      menu={{ items: getActionItems(notification) }}
                      trigger={['click']}
                      key="actions"
                    >
                      <Button type="text" icon={<MoreOutlined />} />
                    </Dropdown>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <div style={{ position: 'relative' }}>
                        <Avatar
                          icon={getCategoryIcon(notification.category || 'system')}
                          style={{ backgroundColor: getPriorityColor(notification.priority || 'medium') }}
                        />
                        <div 
                          style={{ 
                            position: 'absolute', 
                            top: -4, 
                            right: -4,
                            fontSize: '12px'
                          }}
                        >
                          {getTypeIcon(notification.type)}
                        </div>
                      </div>
                    }
                    title={
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Text 
                          strong={!notification.read}
                          style={{ fontSize: '14px' }}
                        >
                          {notification.title}
                        </Text>
                        {notification.action_required && (
                          <Tag color="orange">
                            Action requise
                          </Tag>
                        )}
                        {notification.priority === 'urgent' && (
                          <Tag color="red">
                            Urgent
                          </Tag>
                        )}
                        {!notification.read && (
                          <Badge status="processing" />
                        )}
                      </div>
                    }
                    description={
                      <div>
                        <Text type="secondary" style={{ display: 'block', marginBottom: '4px' }}>
                          {notification.description}
                        </Text>
                        <Space size="small">
                          <Text type="secondary" style={{ fontSize: '11px' }}>
                            {formatDate(notification.created_at)}
                          </Text>
                          {notification.tenant_name && (
                            <>
                              <Text type="secondary" style={{ fontSize: '11px' }}>•</Text>
                              <Text type="secondary" style={{ fontSize: '11px' }}>
                                {notification.tenant_name}
                              </Text>
                            </>
                          )}
                        </Space>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
            
            {/* Pagination */}
            {pagination && pagination.total > pageSize && (
              <div style={{ textAlign: 'center', marginTop: '24px' }}>
                <Button.Group>
                  <Button 
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Précédent
                  </Button>
                  <Button disabled>
                    Page {currentPage} sur {pagination.pages}
                  </Button>
                  <Button 
                    disabled={currentPage === pagination.pages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Suivant
                  </Button>
                </Button.Group>
                <div style={{ marginTop: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, pagination.total)} sur {pagination.total} notifications
                  </Text>
                </div>
              </div>
            )}
          </>
        ) : (
          <Empty 
            description="Aucune notification trouvée"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Card>

      {/* Modal de détails */}
      <Modal
        title="Détails de la notification"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Fermer
          </Button>,
        ]}
        width={600}
      >
        {selectedNotification && (
          <div>
            <Space style={{ marginBottom: '16px' }}>
              {getTypeIcon(selectedNotification.type)}
              <Title level={4} style={{ margin: 0 }}>
                {selectedNotification.title}
              </Title>
              <Tag color={getPriorityColor(selectedNotification.priority || 'medium')}>
                {(selectedNotification.priority || 'medium').toUpperCase()}
              </Tag>
            </Space>
            
            <div style={{ marginBottom: '16px' }}>
              <Text strong>Description:</Text>
              <br />
              <Text>{selectedNotification.description}</Text>
            </div>

            {selectedNotification.details && (
              <div style={{ marginBottom: '16px' }}>
                <Text strong>Détails:</Text>
                <br />
                <Text>{selectedNotification.details}</Text>
              </div>
            )}

            <Row gutter={16}>
              <Col span={12}>
                <Text strong>Catégorie:</Text>
                <br />
                <Space>
                  {getCategoryIcon(selectedNotification.category || 'system')}
                  <Text>{selectedNotification.category || 'system'}</Text>
                </Space>
              </Col>
              <Col span={12}>
                <Text strong>Date:</Text>
                <br />
                <Text>{formatDate(selectedNotification.created_at)}</Text>
              </Col>
            </Row>

            {selectedNotification.tenant_name && (
              <div style={{ marginTop: '16px' }}>
                <Text strong>Entreprise concernée:</Text>
                <br />
                <Text>{selectedNotification.tenant_name}</Text>
              </div>
            )}

            {selectedNotification.action_required && (
              <div style={{ 
                marginTop: '16px', 
                padding: '12px', 
                backgroundColor: '#fff7e6', 
                borderRadius: '4px',
                border: '1px solid #ffd591'
              }}>
                <WarningOutlined style={{ color: '#faad14', marginRight: '8px' }} />
                <Text strong style={{ color: '#faad14' }}>
                  Cette notification nécessite une action de votre part.
                </Text>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SuperAdminNotifications;